/**
 * Kokochao V15 Alpha 2.3 global compatibility runtime.
 * Cross-page only: wishlist badge counts, Judge.me locale fallback,
 * Nouveautés canonical sort and Shopify Inbox positioning.
 */
(() => {
  'use strict';

  const WISHLIST_KEY = 'kokochao_wishlist_handles';
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let chatFrame = 0;

  const onIdle = (callback) => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 1400 });
    else window.setTimeout(callback, 180);
  };

  const readWishlist = () => {
    try {
      const value = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (_) {
      return [];
    }
  };

  function updateWishlistCounts() {
    const count = readWishlist().length;
    document.querySelectorAll('[data-kc-wishlist-count]').forEach((element) => {
      element.textContent = String(count);
      element.classList.toggle('is-visible', count > 0);
    });
  }

  const judgeTranslations = [
    ['No reviews', 'Aucun avis'],
    ['Customer Reviews', 'Avis clients'],
    ['Be the first to write a review', 'Soyez le premier à laisser un avis'],
    ['Write a review', 'Écrire un avis'],
    ['Reviews', 'Avis'],
    ['Review', 'Avis'],
    ['Verified', 'Vérifié'],
    ['Load More', 'Afficher plus'],
    ['See more', 'Voir plus'],
    ['See less', 'Voir moins'],
  ];

  function translateJudgeMe(root) {
    if (!(root instanceof Element)) return;
    const widgets = [];
    if (root.matches('.jdgm-widget,.jdgm-rev-widg,.jdgm-preview-badge')) widgets.push(root);
    root.querySelectorAll?.('.jdgm-widget,.jdgm-rev-widg,.jdgm-preview-badge').forEach((item) => widgets.push(item));
    widgets.forEach((widget) => {
      const walker = document.createTreeWalker(widget, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        let value = node.nodeValue || '';
        judgeTranslations.forEach(([from, to]) => {
          if (value.includes(from)) value = value.split(from).join(to);
        });
        node.nodeValue = value;
      });
    });
  }

  function enforceNewProductsSort() {
    const collection = document.querySelector('collection-component[data-kc-collection-handle="nouveautes"]');
    if (!collection) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('sort_by')) {
      url.searchParams.set('sort_by', 'created-descending');
      window.location.replace(url.toString());
    }
  }

  function looksLikeChat(element) {
    if (!(element instanceof Element)) return false;
    const signature = [
      element.id,
      typeof element.className === 'string' ? element.className : '',
      element.getAttribute('title'),
      element.getAttribute('src'),
      element.getAttribute('aria-label'),
    ].filter(Boolean).join(' ');
    return /(shopify\s*chat|shopify\s*inbox|shopifychat|inbox-chat|chat-widget|\bchat\b)/i.test(signature);
  }

  function containsChat(element) {
    if (!(element instanceof Element)) return false;
    return looksLikeChat(element) || Boolean(element.querySelector(
      '#ShopifyChat, #shopify-chat, shopify-chat, [data-shopify-chat], iframe[src*="shopify-chat" i], iframe[src*="shopifychat" i], iframe[src*="inbox" i], iframe[title*="chat" i]'
    ));
  }

  function fixedHostFor(element) {
    let current = element;
    for (let index = 0; current && index < 6; index += 1, current = current.parentElement) {
      try {
        if (window.getComputedStyle(current).position === 'fixed') return current;
      } catch (_) {
        return element;
      }
    }
    return element;
  }

  function syncChatPosition() {
    chatFrame = 0;
    if (!mobileQuery.matches) return;
    const stickyVisible = Array.from(document.querySelectorAll('.kc-sticky-cart')).some(
      (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && window.getComputedStyle(element).display !== 'none',
    );
    const overlayOpen = Boolean(document.querySelector('theme-drawer[open], dialog[open], cart-drawer.active, cart-drawer[open]'));
    const bottom = stickyVisible
      ? 'calc(158px + env(safe-area-inset-bottom, 0px))'
      : 'calc(92px + env(safe-area-inset-bottom, 0px))';
    const candidates = new Set([
      ...document.querySelectorAll('#ShopifyChat, #shopify-chat, shopify-chat, [data-shopify-chat], iframe[src*="shopify-chat" i], iframe[src*="shopifychat" i], iframe[src*="inbox" i], iframe[title*="chat" i]'),
      ...Array.from(document.querySelectorAll('iframe')).filter(looksLikeChat),
    ]);
    candidates.forEach((candidate) => {
      const host = fixedHostFor(candidate);
      host.style.setProperty('bottom', bottom, 'important');
      host.style.setProperty('transition', 'opacity .18s ease, visibility .18s ease', 'important');
      if (overlayOpen) {
        host.style.setProperty('opacity', '0', 'important');
        host.style.setProperty('visibility', 'hidden', 'important');
        host.style.setProperty('pointer-events', 'none', 'important');
      } else {
        host.style.removeProperty('opacity');
        host.style.removeProperty('visibility');
        host.style.removeProperty('pointer-events');
      }
    });
  }

  function scheduleChatSync() {
    if (chatFrame) cancelAnimationFrame(chatFrame);
    chatFrame = requestAnimationFrame(syncChatPosition);
  }

  function init() {
    updateWishlistCounts();
    enforceNewProductsSort();
    onIdle(() => {
      translateJudgeMe(document.body);
      scheduleChatSync();
    });

    window.addEventListener('kc:wishlist-changed', updateWishlistCounts);
    window.addEventListener('resize', scheduleChatSync, { passive: true });
    window.addEventListener('orientationchange', scheduleChatSync, { passive: true });
    document.addEventListener('theme-drawer:open', scheduleChatSync);
    document.addEventListener('theme-drawer:close', scheduleChatSync);
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element) translateJudgeMe(event.target);
      scheduleChatSync();
    });

    new MutationObserver((mutations) => {
      let chatAdded = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('.jdgm-widget,.jdgm-rev-widg,.jdgm-preview-badge') || node.querySelector('.jdgm-widget,.jdgm-rev-widg,.jdgm-preview-badge')) {
            translateJudgeMe(node);
          }
          if (!chatAdded && containsChat(node)) chatAdded = true;
        });
      });
      if (chatAdded) scheduleChatSync();
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
